import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContact } from '../../hooks/useContact';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import AuthLayout from './AuthLayout';
import AuthFormField, { authInputWithIconClass } from './AuthFormField';
import AuthPasswordField from './AuthPasswordField';
import { cardTheme } from '../../styles/cardTheme';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isMaxDevicesError, setIsMaxDevicesError] = useState(false);
  const { login, isLoading, checkEnrolledCourses } = useAuth();
  const { openWhatsApp, whatsappNumber } = useContact();
  const navigate = useNavigate();
  const location = useLocation();

  const normalizeEmailOrMobile = (value: string): string => {
    const trimmed = value.trim();
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.match(/^20[0-9]{10}$/)) return '0' + digitsOnly.slice(2);
    if (digitsOnly.match(/^0[0-9]{10}$/)) return digitsOnly;
    if (digitsOnly.match(/^1[0-9]{9}$/)) return '0' + digitsOnly;
    return trimmed;
  };

  const validateEmailOrMobile = (value: string) => {
    const trimmed = value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^01[0-9]{9}$/;
    const digitsOnly = trimmed.replace(/\D/g, '');
    const mobileIntlRegex = /^(20)?1[0-9]{9}$/;
    return emailRegex.test(trimmed) || mobileRegex.test(trimmed) || mobileIntlRegex.test(digitsOnly);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsMaxDevicesError(false);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('يرجى إدخال البريد الإلكتروني أو رقم الهاتف');
      return;
    }
    if (!validateEmailOrMobile(trimmedEmail)) {
      setError('يرجى إدخال بريد إلكتروني صحيح أو رقم هاتف مصري صحيح (مثال: 01012345678)');
      return;
    }

    const normalizedInput = normalizeEmailOrMobile(trimmedEmail);
    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      setError('يرجى إدخال كلمة المرور');
      return;
    }

    try {
      const result = await login(normalizedInput, trimmedPassword);

      if (result.success) {
        setSuccessMessage('تم تسجيل الدخول بنجاح!');

        const userId = result.userId;
        let hasCourses = await checkEnrolledCourses({
          forceRefresh: true,
          ...(userId && { userId }),
        });
        if (!hasCourses && userId) {
          await new Promise((r) => setTimeout(r, 400));
          hasCourses = await checkEnrolledCourses({ forceRefresh: true, userId });
        }

        setTimeout(() => {
          const redirectTo = location.state?.redirectTo;
          if (redirectTo) {
            navigate(redirectTo, {
              state: {
                course: location.state?.course,
                courseId: location.state?.courseId,
              },
            });
          } else if (hasCourses) {
            navigate('/student-portal', { replace: true });
          } else {
            navigate('/student-portal', { replace: true });
          }
        }, 1000);
      } else if (result.isMaxDevicesError) {
        setIsMaxDevicesError(true);
      } else {
        setError(result.error || 'حدث خطأ في تسجيل الدخول');
      }
    } catch {
      setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
    }
  };

  const handleRetry = () => {
    setError('');
    setIsMaxDevicesError(false);
  };

  const handleContactSupport = () => {
    if (whatsappNumber) {
      openWhatsApp('مرحباً، أريد المساعدة في مشكلة تسجيل الدخول');
    }
  };

  return (
    <AuthLayout
      title="أهلاً بعودتك"
      subtitle="سجّل دخولك لمتابعة دروسك وكورساتك"
      alternateLink={{
        prompt: 'جديد على المنصة؟',
        label: 'إنشاء حساب جديد',
        to: '/register',
      }}
      footerNote="بتسجيل الدخول، أنت توافق على شروط الاستخدام وسياسة الخصوصية"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && !isMaxDevicesError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
            <p className="text-sm text-red-700">{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-2 text-sm font-semibold text-red-600 hover:text-red-800"
            >
              حاول مرة أخرى
            </button>
          </div>
        )}

        {isMaxDevicesError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4" role="alert">
            <h3 className="mb-2 text-sm font-bold text-amber-900">تم تجاوز الحد المسموح به من الأجهزة</h3>
            <p className="mb-3 text-sm text-amber-800">
              لا يمكنك تسجيل الدخول من أكثر من جهازين. يرجى تسجيل الخروج من أحد الأجهزة الأخرى أو التواصل مع الدعم.
            </p>
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-100/60 p-3 text-xs text-amber-900">
              <p className="mb-1 font-semibold">ملاحظة:</p>
              <ul className="list-inside list-disc space-y-0.5 text-amber-800">
                <li>مسح بيانات المتصفح أو ملفات تعريف الارتباط</li>
                <li>استخدام وضع التصفح الخاص</li>
                <li>تسجيل الدخول من متصفح مختلف على نفس الجهاز</li>
              </ul>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleContactSupport}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${cardTheme.btnPrimary}`}
              >
                تواصل مع الدعم
              </button>
              <button
                type="button"
                onClick={handleRetry}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${cardTheme.btnOutline}`}
              >
                حاول مرة أخرى
              </button>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        )}

        <AuthFormField label="البريد الإلكتروني أو رقم الهاتف" htmlFor="login-email" icon={Mail}>
          <input
            id="login-email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputWithIconClass}
            placeholder="example@email.com أو 01012345678"
            autoComplete="username"
          />
        </AuthFormField>

        <AuthPasswordField
          id="login-password"
          label="كلمة المرور"
          value={password}
          onChange={setPassword}
          placeholder="أدخل كلمة المرور"
          autoComplete="current-password"
        />

        <div className="text-left">
          <Link
            to="/forgot-password"
            className="text-sm font-semibold text-green-700 transition-colors hover:text-gold-700"
          >
            نسيت كلمة المرور؟
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`flex w-full min-h-12 items-center justify-center gap-2 rounded-xl py-3 text-base font-semibold shadow-md shadow-green-900/10 transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70 ${cardTheme.btnPrimary}`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              جاري تسجيل الدخول...
            </>
          ) : (
            <>
              تسجيل الدخول
              <ArrowLeft className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Login;
